import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import LegalFooter from "@/components/LegalFooter";
import { getLegal } from "@/lib/legal";
import { useAuth } from "@/lib/auth";
import { getUser } from "@/lib/session";

export default function PrivacyPolicy() {
  const { account } = useAuth();
  const language = account?.language || getUser()?.language || "English";
  const t = getLegal(language);

  const Section = ({ title, children }) => (
    <div>
      <h3 className="text-base font-bold text-[#F1F1F5] mb-2">{title}</h3>
      <p>{children}</p>
    </div>
  );

  return (
    <div className="min-h-[100dvh] bg-[#060817] text-[#E4E4E7] flex flex-col" data-testid="privacy-page">
      <header className="flex items-center justify-between px-5 py-4 border-b border-white/5">
        <Link to="/" className="flex items-center gap-2 text-[#A0A0AB] hover:text-white transition-colors" data-testid="privacy-back">
          <ArrowLeft className="w-4 h-4" /> <BrandLogo className="text-lg" />
        </Link>
      </header>

      <main className="flex-1 w-full max-w-3xl mx-auto px-5 py-10">
        <h1 className="text-3xl sm:text-4xl font-display font-black text-[#F1F1F5] mb-2">{t.privacy}</h1>
        <p className="text-xs text-[#71717A] mb-8">Last updated: June 2026</p>

        <div className="space-y-6 text-sm leading-relaxed text-[#C7C7CF]">
          <Section title="1. What We Collect">
            When you create an account we store your email, username, chosen country and language, and a hashed password (we never store your password in plain text). Guests provide only a display name and country. In rooms we process chat messages, reactions, and watch history to power the experience.
          </Section>
          <Section title="2. How We Use It">
            To run watch parties: syncing playback, showing who’s watching, translating chat, generating AI recaps/host messages, and remembering your watch history. We also keep an anonymous count of members and installs.
          </Section>
          <Section title="3. Local Storage">
            We store your session token and preferences in your browser’s local storage so you stay signed in. Clearing your browser data signs you out.
          </Section>
          <Section title="4. Third-Party Services">
            Chat translation and AI host features are powered by an LLM provider (Anthropic Claude). Video is played via third-party services (e.g. YouTube) under their own privacy policies. We do not sell your data.
          </Section>
          <Section title="5. Legal & Safety">
            We record your acceptance of the Terms of Service (timestamp, language, country) for legal protection, and we retain abuse reports to review and act on violations.
          </Section>
          <Section title="6. Your Rights">
            You can request deletion of your account and associated data at any time by emailing us. We retain data only as long as needed to provide the service.
          </Section>
          <Section title="7. Contact">
            Privacy questions? Email <a href="mailto:support@ibeflix.com" className="text-[#00D9FF] underline">support@ibeflix.com</a>.
          </Section>
        </div>
      </main>

      <LegalFooter language={language} />
    </div>
  );
}
